import {JsonUtil} from "../lib/JsonUtil";

export class CrewData {

    constructor(crews = [], teams = []) {
        this.crews  = crews;
        this.teams  = teams;
        this.lookup = this.createLookup(crews);                     // maps id --> "firstname lastname"
        this.lookupInitials = this.createLookupInitials(crews)      // maps id --> "initials"
        this.lookupCompany = this.createLookupCompany(crews);       // maps id --> "company"
    }

    isEmpty() {
        return this.crews.length === 0 || this.teams.length === 0;
    }

    getCrews() {
        return this.crews;
    }

    getTeams() {
        return this.teams;
    }

    /**
     * Create a lookup object where id maps to "firstname lastname"
     */
    createLookup(data) {
        return data.reduce((acc, { id, role, firstname, lastname, company }) => {
            let label;

            if (role === "contractor") {
                label = company;
            } else {
                label = `${firstname ?? ""} ${lastname ?? ""}`.trim();
            }

            acc[id] = label;
            return acc;
        }, {});
    }

    lookupName(id) {
        if (id === null) {
            return null;
        }
        return this.lookup[id] || null;
    }

    /**
     * Create a lookup object where id maps to "firstname lastname"
     */
    createLookupInitials(data) {
        return data.reduce((acc, { id, initials }) => {
            acc[id] = `${initials}`;
            return acc;
        }, {});
    }

    lookupInitialsName(id) {
        if (id === null) {
            return null;
        }
        return this.lookupInitials[id] || null;
    }

    /**
     * Create a lookup object where id maps to "company"
     */
    createLookupCompany(data) {
        return data.reduce((acc, { id, company }) => {
            acc[id] = `${company}`;
            return acc;
        }, {});
    }

    lookupCompanyName(id) {
        if (id === null) {
            return null;
        }
        return this.lookupCompany[id] || null;
    }

    filterByRole(role) {
        return this.crews.filter(entry => entry.role === role);
    }

    filterByRoles(roles) {
        return this.crews.filter(member => roles.includes(member.role));
    }

    filterByRolesAndActive(roles, active) {
        const safe = value => value?.toString().toLowerCase() || "";

        return this.crews.filter(member =>
            roles.includes(member.role) && member.active === active

        ).sort((a, b) => {
            const last = safe(a.lastname).localeCompare(safe(b.lastname));
            if (last !== 0) return last;

            const first = safe(a.firstname).localeCompare(safe(b.firstname));
            if (first !== 0) return first;

            return safe(a.company).localeCompare(safe(b.company));        });
    }

    teamLeadIds() {
        return this.teams.map(team => team.lead_crew_id);
    }

    teamMemberIds(teamLeadId) {
        const team = this.teams.find(team => team.lead_crew_id === teamLeadId);
        if (!team) {
            return []; // Return an empty array if no matching team is found
        }
        try {
            return JSON.parse(team.member_crew_ids);
        } catch (error) {
            console.error("Error parsing member_crew_ids:", error);
            return [];
        }
    }

    isLeadOfTeam(currentId) {
        const team = this.teams.find(team => team.lead_crew_id === currentId);
        return team ? this.lookupName(team.lead_crew_id) : null;
    }

    isMemberOfTeam(currentId) {
        const team = this.teams.find(team => {
            try {
                return JsonUtil.convertJsonStringToArray(team.member_crew_ids).includes(currentId);
            } catch (error) {
                console.error("Error parsing member_crew_ids:", error);
                return null;
            }
        });
        return team ? this.lookupName(team.lead_crew_id) : null;
    }

    isActive(id) {
        if (id === null || id === undefined) {
            return false;
        }
        const member = this.crews.find(c => c.id === id);
        return member ? Boolean(member.active) : false;
    }

    loginExists(loginName) {
        if (!loginName) return false;
        return this.crews.some(member => member.login_name === loginName);
    }

    initialsExists(initials, id = null ) {
        if (!initials) return false;
        return this.crews.some(member =>
            member.initials === initials && member.id !== id
        );
    }

    getName(id) {
        return this.lookupName(id);
    }

    getNames(input) {
        try {
            // Falls der Input bereits ein Array ist, nutzen wir ihn direkt.
            // Falls es ein String ist, parsen wir ihn.
            const ids = Array.isArray(input)
                ? input
                : JsonUtil.convertJsonStringToArray(input);

            if (ids && ids.length > 0) {
                return ids
                    .map(id => this.lookupName(id))
                    .filter(name => name !== null) // Sicherheitshalber leere Treffer filtern
                    .join(", ");
            }
        } catch (error) {
            console.error("getNames failed with error:", error);
        }
        return ""; // Lieber leerer String als null für die UI
    }

    getInitials(id) {
        return this.lookupInitialsName(id);
    }

    getCompanyName(id) {
        return this.lookupCompanyName(id);
    }

    getAvorSelection(activeOnly=false, naLabel = " ") {
        const selection = this.filterByRoles(['admin', 'office'])
            .filter(member => !activeOnly || member.active)
            .map(member => ({
                value: member.id,
                label: `${member.firstname} ${member.lastname}`
            }));
        return [{ value: -1, label: naLabel }, ...selection];
    }

    getForemanSelection(activeOnly=false, naLabel = " ") {
        const selection = this.filterByRoles(['admin', 'foreman'])
            .filter(member => !activeOnly || member.active)
            .map(member => ({
                value: member.id,
                label: `${member.firstname} ${member.lastname}`
        }));
        return [{ value: -1, label: naLabel }, ...selection];
    }

    getTeamLeadSourceItems(inclAll=false) {
        const items = [
            {label: "Gruppe",         value:0},                 // getTeamLeadGroupSelection
            {label: "Mitarbeiter",    value:1},                 // getTeamLeadWorkerSelection
            {label: "Subunternehmer", value:2}                  // getTeamLeadContractorSelection
        ]

        if (inclAll) {
            items.unshift({ label: "Alle", value: -1 });    // Selection for search only
        }

        return items;
    }

    getTeamLeadSourceLabel(source) {
        const item = this.getTeamLeadSourceItems(true)
            .find(item => item.value === Number(source));
        return item ? item.label : "";
    }

    getTeamLeadItems(source) {
        const nr = Number(source);

        if (nr === -1) {
            return this.getTeamLeadGroupAndContractorSelection();
        } else if (nr === 0) {
            return this.getTeamLeadGroupSelection();
        } else if (nr === 1) {
            return this.getTeamLeadWorkerSelection();
        } else if (nr === 2) {
            return this.getTeamLeadContractorSelection();
        }

        return this.getTeamLeadGroupSelection();
    }

    getTeamLeadGroupSelection() {
        // Get all team lead IDs from the handler
        const teamLeadIds = this.teamLeadIds();

        // Create the selection array
        return teamLeadIds.map(id => ({
            value: id,
            label: this.lookupName(id)
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'de', { sensitivity: 'base' }));
    }

    getTeamLeadWorkerSelection(activeOnly=true) {
        return this.filterByRoles(['foreman', 'worker'])
            .filter(member => !activeOnly || member.active)
            .map(member => ({
                value: member.id,
                label: `${member.firstname} ${member.lastname}`
            }))
            .sort((a, b) => a.label.localeCompare(b.label, 'de', { sensitivity: 'base' }));
    }

    getTeamLeadContractorSelection() {
        return this.filterByRole('contractor')
            .map(member => ({
                value: member.id,
                label: `${member.company}`
            }))
            .sort((a, b) => a.label.localeCompare(b.label, 'de', { sensitivity: 'base' }));
    }

    getTeamLeadGroupAndContractorSelection(naLabel = " ") {
        const group = this.getTeamLeadGroupSelection();
        const contractor = this.getTeamLeadContractorSelection();
        return [{ value: -1, label: naLabel }, ...group, ...contractor];
    }

    getTeamMemberSelection(naLabel = " ") {
        const worker = this.getTeamLeadWorkerSelection();
        return [{ value: -1, label: naLabel }, ...worker];
    }

    getWarrantySelection(activeOnly=true) {
        return this.filterByRoles(['foreman', 'worker', 'contractor'])
            .filter(member => !activeOnly || member.active)
            .map(member => ({
                value: member.id,
                label: member.role === 'contractor' ? `${member.company}` : `${member.firstname} ${member.lastname}`
            }))
            .sort((a, b) => a.label.localeCompare(b.label, 'de', { sensitivity: 'base' }));
    }

    /**
     * Resolve team member id's for a given team lead id
     */
    getTeamMemberIds(teamLeadId) {
        return this.teamMemberIds(teamLeadId);
    }

    getTeamMemberItems(teamMemberIds) {
        let memberIds = [];
        // evaluate to array
        if ( Array.isArray(teamMemberIds) ) {
            memberIds = teamMemberIds;
        } else if ( teamMemberIds instanceof Set) {
            memberIds = [...teamMemberIds]; // Spread operator converts Set into an array
        } else {
            memberIds = JsonUtil.convertJsonStringToArray(teamMemberIds);
        }
        // check for empty array
        if (memberIds.length === 0) {
            return [];
        }
        // create member items
        return memberIds.map(id => ({
            id: id,
            label: this.lookupName(id)
        }));
    }

};